import { s__ } from '~/locale';
import { humanize } from '~/lib/utils/text_utility';
import { localeDateFormat } from '~/lib/utils/datetime/locale_dateformat';
import { isGid, getIdFromGraphQLId } from '~/graphql_shared/utils';
import {
  AGENT_PLATFORM_STATUS_ICON,
  AGENT_PLATFORM_STATUS_BADGE,
  FILTER_FIELD_ACTION,
  FILTER_FIELD_PIPELINE_STATUS,
  FILTER_OPERATOR_IN,
  FLOW_TRIGGER_TYPE_MERGE_REQUEST,
  FLOW_TRIGGER_TYPE_PIPELINE_HOOKS,
  FLOW_TRIGGER_TYPE_WORK_ITEM,
  MERGE_REQUEST_ACTIONS,
  MESSAGE_SUB_TYPE_DELEGATION,
  MESSAGE_SUB_TYPE_DELEGATION_RETURNS,
  PIPELINE_HOOK_STATUSES,
  WORK_ITEM_ACTIONS,
} from './constants';
import { parseToolInfo } from './icon_utils';

export const getNumericId = (id) => {
  if (!id) return null;
  return isGid(id) ? getIdFromGraphQLId(id) : id;
};

export { getToolData, getMessageData } from './icon_utils';

export const formatAgentDefinition = (agentDefinition) => {
  return humanize(agentDefinition || s__('DuoAgentsPlatform|Agent session'));
};

export const formatAgentFlowName = (agentDefinition, id) => {
  return `${formatAgentDefinition(agentDefinition)} #${id}`;
};

export const formatAgentFlowTitle = (title, agentDefinition) =>
  title?.trim() ? title : formatAgentDefinition(agentDefinition);

export const formatAgentFlowTitleWithId = (title, agentDefinition, id) =>
  title?.trim() ? title : formatAgentFlowName(agentDefinition, id);

export const formatAgentStatus = (status) => {
  return status ? humanize(status.toLowerCase()) : s__('DuoAgentsPlatform|Unknown');
};

export const getAgentStatusIcon = (status) => {
  return AGENT_PLATFORM_STATUS_ICON[status] || AGENT_PLATFORM_STATUS_ICON.CREATED;
};

export const getAgentStatusBadge = (status) => {
  return AGENT_PLATFORM_STATUS_BADGE[status] || AGENT_PLATFORM_STATUS_BADGE.FAILED;
};

export const getNamespaceDatasetProperties = (dataset, properties) =>
  properties.reduce((acc, prop) => {
    acc[prop] = dataset[prop];
    return acc;
  }, {});

/**
 * Extracts component identity from a message for display.
 * Returns null when component attribution is not available (backward compat).
 */
export const getComponentLabel = (message) => {
  if (!message?.componentName) return null;
  return {
    componentName: message.componentName,
    subsessionId: message.subsessionId ?? null,
    isSubagent: Boolean(message.subsessionId),
  };
};

export const getDelegationData = (message) => {
  const toolInfo = parseToolInfo(message?.toolInfo);

  if (!toolInfo || toolInfo.name !== 'delegate_task') {
    return null;
  }

  return {
    subagentName: toolInfo.args?.subagent_name ?? null,
    subsessionId: toolInfo.args?.subsession_id?.toString() ?? null,
    prompt: toolInfo.args?.prompt ?? null,
  };
};

/**
 * Maps each unique `componentName` in `items` to a stable colour index based
 * on first-seen order. The same component name always gets the same index,
 * and a new name gets the next available index.
 *
 * Pure function so the algorithm can be unit-tested without mounting the
 * activity log component.
 */
export const buildComponentColorIndexMap = (items) => {
  return items.reduce((acc, item) => {
    const name = item?.componentName;
    if (name && !(name in acc)) {
      acc[name] = Object.keys(acc).length;
    }
    return acc;
  }, {});
};

/**
 * Maps each item index to its nesting depth. Entries between a delegation
 * and its matching `delegation_returns` are indented one level to visually
 * group subagent work.
 *
 * Pure function so the algorithm can be unit-tested without mounting the
 * activity log component.
 */
export const computeItemDepths = (items) => {
  let depth = 0;
  return items.map((item) => {
    if (item?.messageSubType === MESSAGE_SUB_TYPE_DELEGATION) {
      const current = depth;
      depth += 1;
      return current;
    }

    if (item?.messageSubType === MESSAGE_SUB_TYPE_DELEGATION_RETURNS) {
      depth = Math.max(0, depth - 1);
      return depth;
    }

    return depth;
  });
};

export const formatDate = (isoString) => {
  if (!isoString) return '';

  try {
    return localeDateFormat.asDate.format(new Date(isoString));
  } catch {
    return '';
  }
};

/**
 * Extracts the values of a single `{ field, operator: 'in', value: [...] }` rule from a
 * trigger filter scope, keeping only values that belong to the known option set.
 *
 * The filter is shaped like `{ <scope>: { rules: [{ field, operator, value }] } }`
 * (validated by app/validators/json_schemas/filter.json).
 * Returns `[]` when the filter is missing, malformed, or has values outside `options`.
 */
export const parseFilterRuleValues = ({ filter, scope, field, options }) => {
  const rules = filter?.[scope]?.rules;
  if (!Array.isArray(rules) || rules.length !== 1) return [];

  const [rule] = rules;
  if (rule.field !== field || rule.operator !== FILTER_OPERATOR_IN || !Array.isArray(rule.value)) {
    return [];
  }

  const allowedValues = options.map((option) => option.value);
  return rule.value.filter((value) => allowedValues.includes(value));
};

/**
 * Maps selected option values to their human-readable labels, preserving option order.
 * Used to render the trigger token summary on the catalog item page.
 */
const selectedOptionLabels = (options, values) =>
  options.filter((option) => values.includes(option.value)).map((option) => option.text);

export const parsePipelineStatusFilter = (filter) =>
  parseFilterRuleValues({
    filter,
    scope: FLOW_TRIGGER_TYPE_PIPELINE_HOOKS.value,
    field: FILTER_FIELD_PIPELINE_STATUS,
    options: PIPELINE_HOOK_STATUSES,
  });

export const pipelineStatusLabels = (filter) =>
  selectedOptionLabels(PIPELINE_HOOK_STATUSES, parsePipelineStatusFilter(filter));

export const parseMergeRequestActionFilter = (filter) =>
  parseFilterRuleValues({
    filter,
    scope: FLOW_TRIGGER_TYPE_MERGE_REQUEST.value,
    field: FILTER_FIELD_ACTION,
    options: MERGE_REQUEST_ACTIONS,
  });

export const mergeRequestActionLabels = (filter) =>
  selectedOptionLabels(MERGE_REQUEST_ACTIONS, parseMergeRequestActionFilter(filter));

export const parseWorkItemActionFilter = (filter) =>
  parseFilterRuleValues({
    filter,
    scope: FLOW_TRIGGER_TYPE_WORK_ITEM.value,
    field: FILTER_FIELD_ACTION,
    options: WORK_ITEM_ACTIONS,
  });

export const workItemActionLabels = (filter) =>
  selectedOptionLabels(WORK_ITEM_ACTIONS, parseWorkItemActionFilter(filter));
